'use client';

import { useState, useEffect, createContext, useContext } from "react";
import { UserOption } from "@/types/user";
import { onAuthStateChanged } from "@/lib/authState";
import { auth, db } from "@/lib/firebase";
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";
import { onAuthReady } from "@/lib/authState"
import { getUserInfo } from "@/services/userService";
import { getDoc, doc } from "firebase/firestore";
interface UserContextType {
    user: UserOption[];
    setUser: (user: UserOption[]) => void;
}

const UserContext = createContext<UserContextType>({
    user: [],
    setUser: () => { }
});

export function UserContextProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<UserOption[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Initial user info fetch
        const fetchUser = async () => {
            try {
                const userInfo = await getUserInfo();
                if (userInfo) {
                    setUser([{
                        id: userInfo.uid,
                        name: userInfo.displayName || '',
                        email: userInfo.email || '',
                        photoURL: userInfo.photoURL || ''
                    }]);;
                    /*
                      configure window.hsConversationsSettings if needed.
                    */
                    (window as any).hsConversationsSettings = {
                        loadImmediately: false,
                    };

                    // Wait for auth to be ready before configuring HubSpot
                    onAuthReady(async () => {
                        // Get real user info and configure HubSpot
                        if (!userInfo) {
                            console.error('Unable to get user info for HubSpot configuration');
                            return;
                        }

                        // Get info from user profile in Firestore
                        const userDoc = await getDoc(doc(db, 'users', userInfo.uid));
                        const userData = userDoc.data();

                        // Build the best name from available sources
                        let userName = userInfo.displayName || '';
                        if (!userName || userName.trim() === '') {
                            // Try to build from Firestore firstName/lastName
                            const firstName = userData?.firstName || '';
                            const lastName = userData?.lastName || '';
                            userName = `${firstName} ${lastName}`.trim();
                        }
                        
                        // Fallback to email username if still no name
                        if (!userName) {
                            userName = userInfo.email?.split('@')[0] || 'User';
                        }

                        // Build email with fallback
                        const userEmail = userInfo.email || userData?.email || '';
                        
                        // Build photoURL with fallback
                        const userPhoto = userInfo.photoURL || userData?.photoURL || '';

                        setUser([{
                            id: userInfo.uid,
                            name: userName,
                            email: userEmail,
                            photoURL: userPhoto,
                            ...userData,
                        }])

                        const hubSpotUserData = {
                            "email": userInfo.email || "",
                            "firstName": userInfo.displayName?.split(' ')[0] || userInfo.firstName || "",
                            "lastName": userInfo.displayName?.split(' ').slice(1).join(' ') || userInfo.lastName || "",
                            "language": userInfo.language || "en",
                            "timezone": userInfo.timezone || "Australia/Sydney"
                        };

                        deiterateAPI.POST('/hubspot-token', {
                            body: JSON.stringify(hubSpotUserData)
                        }).then(data => {
                            if (!data) return; // Skip if getUserInfo failed

                            // Store token for custom chat but don't load the widget
                            (window as any).hubspotVisitorToken = data.token;
                            (window as any).hubspotUserEmail = hubSpotUserData.email;
                            
                            console.log('[HubSpot] Token stored for custom chat integration');
                            
                            // Don't load the HubSpot widget script since we're using custom chat
                            // If you want to re-enable the widget, uncomment the code below:
                            
                            
                            // Create dynamic script tag
                            const script = document.createElement('script');
                            script.type = 'text/javascript';
                            script.id = 'hs-script-loader';
                            script.async = true;
                            script.defer = true;
                            script.src = '//js-eu1.hs-scripts.com/144683379.js';
                            document.head.appendChild(script);

                            if ((window as any).HubSpotConversations) {
                                onConversationsAPIReady();
                            }
                            else {
                                (window as any).hsConversationsOnReady = [onConversationsAPIReady];
                            }

                            function onConversationsAPIReady() {
                                var emailAddress = hubSpotUserData.email;
                                var token = data.token;

                                (window as any).hsConversationsSettings = {
                                    disableAttachment: true,
                                    loadImmediately: false,
                                    identificationEmail: emailAddress,
                                    identificationToken: token
                                };
                            }

                            function loadChat() {
                                const status = (window as any).HubSpotConversations?.widget?.status();

                                if (status?.loaded) {
                                    (window as any).HubSpotConversations?.widget?.refresh();
                                } else {
                                    (window as any).HubSpotConversations?.widget?.load();
                                }
                            }

                            setTimeout(() => {
                                loadChat();
                            }, 5000);
                            
                        });
                    });
                }
            } catch (error) {
                console.error('Error fetching user info:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();

        // Auth state listener
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser([{
                    id: user.uid,
                    name: user.displayName || '',
                    email: user.email || '',
                    photoURL: user.photoURL || ''
                }]);
            } else {
                setUser([]);
            }
        });

        return () => unsubscribe();
    }, []);

    // Don't render children until we've finished the initial auth check
    if (loading) {
        return null;
    }

    return (
        <UserContext.Provider value={{ user, setUser }}>
            {children}
        </UserContext.Provider>
    );
}

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserContextProvider');
    }
    return context;
};
